import React from "react"
import { Card, Typography, Divider, Flex, Tag } from "antd"
import { EyeOutlined } from "@ant-design/icons"
import { FEE_PAYER_MODE } from "../hooks/usePlatformFees"
import PropTypes from "prop-types"
import styles from "./CheckoutPreview.module.css"

const { Title, Text } = Typography

const PreviewRow = ({ label, amount, highlight, tag }) => (
  <Flex align="center" justify="space-between" className={`${styles.previewRow} ${highlight ? styles.totalRow : ""}`}>
    <Flex align="center" gap={6}>
      <Text className={highlight ? styles.boldText : styles.rowLabel}>{label}</Text>
      {tag && (
        <Tag color="blue" className={styles.lineItemTag}>
          Line Item
        </Tag>
      )}
    </Flex>
    <Text className={highlight ? styles.boldAmount : styles.rowAmount}>{amount}</Text>
  </Flex>
)

PreviewRow.propTypes = {
  label: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  highlight: PropTypes.bool,
  tag: PropTypes.bool
}

const CheckoutPreview = ({
  customFees = [],
  lineItemToggles = {},
  feePayerMode,
  passFeeToGuest,
  platformFeeAsLineItem
}) => {
  const activeFees = customFees.filter((f) => f.is_active)

  const separateFees = activeFees.filter((f) => lineItemToggles[f.id])
  const combinedFees = activeFees.filter((f) => !lineItemToggles[f.id])

  const hasCombined = combinedFees.length > 0
  const showPlatformFee = platformFeeAsLineItem && feePayerMode !== FEE_PAYER_MODE.GUIDE_PAYS
  const platformFeeInTaxAndFees = !platformFeeAsLineItem && feePayerMode !== FEE_PAYER_MODE.GUIDE_PAYS

  return (
    <Card className={styles.previewCard}>
      <Flex align="center" gap={8} className={styles.previewHeader}>
        <EyeOutlined className={styles.eyeIcon} />
        <Title level={5} className={styles.previewTitle}>
          Checkout Preview
        </Title>
      </Flex>
      <Divider className={styles.divider} />

      <Flex vertical gap={6}>
        <PreviewRow label="Trip Subtotal" amount="$0" />

        {separateFees.map((fee) => (
          <PreviewRow key={fee.id} label={fee.title} amount="$0" tag />
        ))}

        {showPlatformFee && <PreviewRow label="Platform Fee" amount="$0" tag />}

        {(hasCombined || platformFeeInTaxAndFees) && <PreviewRow label="Taxes & Fees" amount="$0" />}

        <PreviewRow label="Total" amount="$0" highlight />

        {/* The card processing fee is never rolled into Taxes & Fees: it is charged on the total
            once card payment is chosen, so it always sits on its own line below the total. */}
        {passFeeToGuest && <PreviewRow label="Payment Processing Fee" amount="$0" tag />}
      </Flex>

      {activeFees.length === 0 && (
        <div className={styles.emptyNote}>
          <Text type="secondary">Add custom fees to see how they appear at checkout.</Text>
        </div>
      )}
    </Card>
  )
}

CheckoutPreview.propTypes = {
  customFees: PropTypes.array,
  lineItemToggles: PropTypes.object,
  feePayerMode: PropTypes.string,
  passFeeToGuest: PropTypes.bool,
  platformFeeAsLineItem: PropTypes.bool
}

export default CheckoutPreview
